import * as React from 'react';

import { ScrollContainerTarget } from '../utils/scrollContainerTarget';

import type { ScrollController } from './horizontalScroll/types';
import { ScrollZone } from './horizontalScroll/types';
import useHasHorizontalScroll from './useHasHorizontalScroll';
import useHorizontalScrollArrows from './useHorizontalScrollArrows';
import useHorizontalScrollAttach from './useHorizontalScrollAttach';

export { ScrollZone } from './horizontalScroll/types';
export type { ScrollController } from './horizontalScroll/types';

const DEFAULT_SMOOTH_FACTOR = 0.12;
const DEFAULT_SCROLLBAR_ZONE_HEIGHT = 24;

export type UseHorizontalScrollControlOptions = {
  enabled?: boolean;
  wheelScroll?: boolean;
  /** Track canScrollLeft/Right and wire handleLeft/Right; requires scrollStep or autoScrollStep */
  arrows?: boolean;
  zone?: ScrollZone;
  scrollbarZoneHeight?: number;
  smoothFactor?: number;
  /** Fixed step in px; ignored when autoScrollStep is true */
  scrollStep?: number;
  /** Snap flex items to the left edge on arrow click */
  autoScrollStep?: boolean;
  /** Flex row with scroll items; observed for resize when updateOnResize is true */
  itemsTrackRef?: ScrollContainerTarget;
  updateOnResize?: boolean;
  /** Show hasHorizontalScroll when content overflows (e.g. custom scroll rail) */
  trackHasHorizontalScroll?: boolean;
  /** Deps tracked for hasHorizontalScroll recalculation */
  hasHorizontalScrollDeps?: readonly unknown[];
};

const useHorizontalScrollControl = (
  target: ScrollContainerTarget,
  options: UseHorizontalScrollControlOptions = {}
) => {
  const {
    enabled = true,
    wheelScroll = false,
    arrows = false,
    zone = ScrollZone.full,
    scrollbarZoneHeight = DEFAULT_SCROLLBAR_ZONE_HEIGHT,
    smoothFactor = DEFAULT_SMOOTH_FACTOR,
    scrollStep,
    autoScrollStep = false,
    itemsTrackRef,
    updateOnResize = false,
    trackHasHorizontalScroll = false,
    hasHorizontalScrollDeps = []
  } = options;

  const controllerRef = React.useRef<ScrollController | null>(null);

  const hasHorizontalScroll = useHasHorizontalScroll(target, {
    enabled: enabled && trackHasHorizontalScroll,
    deps: hasHorizontalScrollDeps
  });

  const {
    canScrollLeft,
    canScrollRight,
    handleLeft,
    handleRight,
    updateScrollButtons
  } = useHorizontalScrollArrows({
    enabled: enabled && arrows,
    target,
    itemsTrackRef,
    updateOnResize,
    autoScrollStep,
    scrollStep,
    controllerRef
  });

  useHorizontalScrollAttach({
    target,
    enabled,
    wheelScroll,
    arrows,
    zone,
    scrollbarZoneHeight,
    smoothFactor,
    controllerRef,
    onScroll: updateScrollButtons
  });

  return {
    canScrollLeft: arrows ? canScrollLeft : false,
    canScrollRight: arrows ? canScrollRight : true,
    hasHorizontalScroll,
    handleLeft,
    handleRight
  };
};

export default useHorizontalScrollControl;
